/**
 * Bookkeeping export service.
 *
 * Builds a flat per-booking row shape suitable for accountants. Rows include
 * monetary totals, payment & refund roll-ups, deposits, item names and basic
 * customer / location identifiers. One row per booking — item-level detail
 * can be added as a separate export later if asked for.
 *
 * The query is paginated with a hard cap so the export still works for
 * merchants with thousands of bookings in the selected range without
 * silently hitting PostgREST's default row limit. If the cap is reached the
 * caller receives `capped: true` so it can warn the user that the file is
 * truncated.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import { applyAccessControl, type AccessControl } from '../lib/accessControl';

const PAGE_SIZE = 500;
const HARD_CAP = 25000;

export interface BookkeepingFilters {
  startDate?: string;
  endDate?: string;
  locationId?: string;
  /** 'rental' filters by start_date; 'booking' filters by created_at. */
  dateBasis?: 'booking' | 'rental';
}

export interface BookkeepingRow {
  order_id: string | null;
  booking_date: string | null;
  rental_start: string | null;
  rental_end: string | null;
  returned_at: string | null;
  state: string | null;
  location_name: string | null;
  customer_name: string;
  customer_email: string | null;
  products: string;
  currency: string | null;
  // Direct booking columns: preserve null. A null subtotal in the DB means
  // "no value recorded" and is meaningfully different from a zero subtotal.
  // The `|| 0` fallback the previous version used hid that distinction and
  // gave accountants false zeros for older / partially-populated bookings.
  subtotal_net: number | null;
  discount: number | null;
  tax_rate: number | null;
  tax_amount: number | null;
  tax_included: boolean | null;
  total_gross: number | null;
  // Computed values: always a number. Zero means "received nothing", which
  // is semantically different from "we don't know what was received".
  amount_paid: number;
  amount_refunded: number;
  net_received: number;
  last_payment_date: string | null;
  payment_channels: string;
  deposit_amount: number | null;
  deposit_captured: number | null;
  deposit_status: string | null;
}

function nullableNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export interface BookkeepingResult {
  data: BookkeepingRow[] | null;
  error: string | null;
  /** True when the hard cap was reached and the result is truncated. */
  capped?: boolean;
}

function buildBookingsQuery(
  accessControl: AccessControl | null,
  filters: BookkeepingFilters,
  dateColumn: 'created_at' | 'start_date',
) {
  let query = supabase
    .from('bookings')
    .select(`
      id,
      order_id,
      created_at,
      start_date,
      end_date,
      returned_at,
      booking_state,
      customer_firstname,
      customer_lastname,
      customer_email,
      currency,
      subtotal,
      total_discount,
      tax_rate,
      total_tax,
      tax_included,
      total_amount,
      amount_paid,
      deposit_amount,
      deposit_captured_amount,
      deposit_status,
      location_id,
      locations!inner(merchant_id, location_name),
      booking_items(item_name, quantity),
      payment_transactions(amount, channel, created_at),
      refund_history(refund_amount)
    `)
    // `id` is the unique tie-breaker for the paging below. Neither created_at
    // nor start_date is unique — bulk-imported bookings routinely share one —
    // and rows with an equal sort key have no defined order between two
    // requests, so without it a row can appear on both sides of a page
    // boundary or on neither.
    .order(dateColumn, { ascending: true })
    .order('id', { ascending: true });

  query = applyAccessControl(query, accessControl, {
    merchantIdColumn: 'locations.merchant_id',
    locationIdColumn: 'location_id',
  });

  if (filters.locationId) {
    query = query.eq('location_id', filters.locationId);
  }
  if (filters.startDate) {
    query = query.gte(dateColumn, filters.startDate);
  }
  if (filters.endDate) {
    query = query.lte(dateColumn, filters.endDate);
  }
  return query;
}

function rowFromBooking(b: any): BookkeepingRow {
  const transactions: Array<{ amount: number | null; channel: string | null; created_at: string | null }> =
    Array.isArray(b.payment_transactions) ? b.payment_transactions : [];
  const refunds: Array<{ refund_amount: number | null }> =
    Array.isArray(b.refund_history) ? b.refund_history : [];
  const items: Array<{ item_name: string | null; quantity: number | null }> =
    Array.isArray(b.booking_items) ? b.booking_items : [];

  const amountPaid = transactions.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
  const amountRefunded = refunds.reduce((sum, r) => sum + (Number(r.refund_amount) || 0), 0);

  const lastPayment = transactions
    .map((tx) => tx.created_at)
    .filter((d): d is string => !!d)
    .sort()
    .pop() || null;

  const channels = Array.from(
    new Set(transactions.map((tx) => tx.channel).filter((c): c is string => !!c))
  ).sort();

  const products = items
    .map((i) => {
      if (!i.item_name) return '';
      const qty = Number(i.quantity);
      return qty > 1 ? `${qty}× ${i.item_name}` : i.item_name;
    })
    .filter(Boolean)
    .join(' | ');

  const customerName = [b.customer_firstname, b.customer_lastname]
    .filter((p: string | null) => p && p.trim())
    .join(' ')
    .trim();

  return {
    order_id: b.order_id ?? null,
    booking_date: b.created_at ?? null,
    rental_start: b.start_date ?? null,
    rental_end: b.end_date ?? null,
    returned_at: b.returned_at ?? null,
    state: b.booking_state ?? null,
    location_name: b.locations?.location_name ?? null,
    customer_name: customerName,
    customer_email: b.customer_email ?? null,
    products,
    currency: b.currency ?? null,
    subtotal_net: nullableNumber(b.subtotal),
    discount: nullableNumber(b.total_discount),
    tax_rate: nullableNumber(b.tax_rate),
    tax_amount: nullableNumber(b.total_tax),
    tax_included: b.tax_included ?? null,
    total_gross: nullableNumber(b.total_amount),
    amount_paid: amountPaid,
    amount_refunded: amountRefunded,
    net_received: Math.max(0, amountPaid - amountRefunded),
    last_payment_date: lastPayment,
    payment_channels: channels.join(' | '),
    deposit_amount: nullableNumber(b.deposit_amount),
    deposit_captured: nullableNumber(b.deposit_captured_amount),
    deposit_status: b.deposit_status ?? null,
  };
}

export async function getBookkeepingRows(
  accessControl: AccessControl | null,
  filters: BookkeepingFilters,
): Promise<BookkeepingResult> {
  try {
    const dateColumn = filters.dateBasis === 'rental' ? 'start_date' : 'created_at';

    const rows: BookkeepingRow[] = [];
    let offset = 0;
    let capped = false;

    while (true) {
      const remainingBudget = HARD_CAP - rows.length;
      if (remainingBudget <= 0) {
        capped = true;
        break;
      }
      const pageSize = Math.min(PAGE_SIZE, remainingBudget);
      const { data, error } = await buildBookingsQuery(accessControl, filters, dateColumn)
        .range(offset, offset + pageSize - 1);
      if (error) throw error;
      const page = data || [];
      for (const b of page) rows.push(rowFromBooking(b));
      if (page.length < pageSize) break;
      offset += pageSize;
    }

    return { data: rows, error: null, capped };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/** Exposed for the UI so it can mention the cap in warning messages. */
export const BOOKKEEPING_HARD_CAP = HARD_CAP;
